// Shared hydration / DOM-mutation error filtering used by ErrorBoundary and logError.
// Both message and stack are inspected so that the filter still catches a hydration
// error even when ErrorEvent.message is empty/wrapped (e.g. cross-origin reportError).

const DOM_MUTATION_NEEDLES = [
  // Chromium/WebKit phrasing
  "Failed to execute 'removeChild'",
  "Failed to execute 'insertBefore'",
  "Failed to execute 'appendChild'",
  // Firefox phrasing (same class of bug: an extension — e.g. Google Translate —
  // mutating the DOM out from under React's reconciler)
  "Node.removeChild:",
  "Node.insertBefore:",
  "Node.appendChild:",
  // Extension nulled/replaced <head> mid-commit, so React's insertBefore hits a
  // null document.head. Only the Firefox phrasing is safely matchable — the Chrome
  // equivalent ("Cannot read properties of null (reading 'insertBefore')") is too
  // generic to filter without risking real bugs.
  ".head is null",
];

const HYDRATION_MISMATCH_NEEDLES = [
  'Minified React error #418',
  'Minified React error #423',
  'Minified React error #425',
  'Text content does not match',
  'Hydration failed because',
];

// Parse-time errors thrown by older JS engines (e.g. iOS 16.x Safari) when they
// hit ES2022+ syntax in our bundles. The legacy-browser-banner script in
// app/layout.tsx already shows the user a banner for these — they should be
// suppressed from error_logs so the dashboard reflects only actionable issues.
// Built-ins that a too-old engine lacks. A ReferenceError naming one of these is the legacy browser, not us;
// a ReferenceError naming anything else ("foo is not defined") is a bug in our code and must be logged.
const LEGACY_MISSING_GLOBALS = new Set([
  'globalThis', 'structuredClone', 'queueMicrotask', 'ResizeObserver', 'IntersectionObserver',
  'AbortController', 'AbortSignal', 'BigInt', 'WeakRef', 'FinalizationRegistry', 'AggregateError',
  'TextEncoder', 'TextDecoder', 'requestIdleCallback', 'cancelIdleCallback', 'BroadcastChannel',
]);

// Browser-extension wallets (MetaMask, Phantom, etc.) auto-inject into every
// page and emit "failed to connect" rejections when they can't find a Web3
// consumer. mcworldtools has no Web3 code — these are pure extension noise.
const WALLET_EXTENSION_NEEDLES = [
  'MetaMask',
  'window.ethereum',
];

// Bare DOMException messages thrown by the browser itself, not by our code. These
// are the WebKit/Safari phrasings that carry NO method name (so the DOM_MUTATION
// needles above, which key on "Failed to execute 'removeChild'" / "Node.removeChild:",
// miss them) plus other transient DOM-state exceptions:
//   • "The object can not be found here."  — NotFoundError: the same
//     removeChild/insertBefore-under-React race an extension causes, just phrased
//     without the method name. Seen 1x each on execute-builder / player-data / uuid-lookup.
//   • "The object is in an invalid state." — InvalidStateError: a transient DOM/
//     canvas/IndexedDB state fault (skin-editor), never a string we author.
// We never throw these exact strings, so matching them can't mask an app error's
// own message. Trade-off (same family as isChunkLoadError / isTransientNetworkError):
// a persistent, reproducible DOM bug that surfaced ONLY as one of these bare
// messages would also be silenced — but those reproduce as user reports, and every
// occurrence to date has been a dormant 1x with no app frames.
const BROWSER_DOM_NOISE_NEEDLES = [
  'The object can not be found here.',
  'The object is in an invalid state.',
];

export function isBrowserDomNoiseError(error: unknown, fallbackMsg = ''): boolean {
  const msg = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
  const stack = error instanceof Error ? (error.stack ?? '') : '';
  return matchesAny(msg, BROWSER_DOM_NOISE_NEEDLES)
      || matchesAny(stack, BROWSER_DOM_NOISE_NEEDLES)
      || matchesAny(fallbackMsg, BROWSER_DOM_NOISE_NEEDLES);
}

function matchesAny(haystack: string, needles: string[]): boolean {
  for (const n of needles) if (haystack.includes(n)) return true;
  return false;
}

export function isDomManipulationError(msg: string): boolean {
  return matchesAny(msg, DOM_MUTATION_NEEDLES);
}

/** Hardened: checks message AND stack AND a fallback string. */
export function isHydrationMismatchError(error: unknown, fallbackMsg = ''): boolean {
  const msg = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
  const stack = error instanceof Error ? (error.stack ?? '') : '';
  return matchesAny(msg, HYDRATION_MISMATCH_NEEDLES)
      || matchesAny(stack, HYDRATION_MISMATCH_NEEDLES)
      || matchesAny(fallbackMsg, HYDRATION_MISMATCH_NEEDLES);
}

export function isBrowserExtensionDomError(error: unknown): boolean {
  const msg = error instanceof Error ? error.message : String(error);
  return isDomManipulationError(msg) || isHydrationMismatchError(error);
}

/**
 * Matches what a TOO-OLD JS engine throws on our bundles — and nothing else. The user already sees a
 * "your browser may be too old" banner (legacy-browser-banner script in app/layout.tsx); this keeps the
 * same errors out of error_logs.
 *
 * ⚠️ It used to match EVERY SyntaxError / ReferenceError, which also silenced real bugs in our own code
 * (a typo'd variable, `res.json()` on an HTML error page). Now:
 *   • SyntaxError: dropped ONLY when the window error event says the browser failed to PARSE one of our
 *     script files — frameless AND `sourceFile` is a /_next/static/ chunk. Only `handleWindowError` has
 *     a source file; every other path (logError, unhandled rejections) logs SyntaxErrors. Anything
 *     mentioning JSON is always logged: Chrome's `res.json()` failure is frameless too.
 *   • ReferenceError: dropped when the missing name is a built-in old engines lack
 *     (LEGACY_MISSING_GLOBALS), or when nothing in it points at our code — no /_next/static/ frame and no
 *     /_next/static/ source file (scripts other software injects: `LIDNotifyId`, `__gCrWeb`). A
 *     ReferenceError with one of OUR frames naming anything else is our bug and is logged.
 * Self-test: npm run verify:errorfilters.
 */
export function isLegacyBrowserSyntaxError(error: unknown, fallbackMsg = '', sourceFile = ''): boolean {
  const msg = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
  const stack = error instanceof Error ? (error.stack ?? '') : '';
  const text = `${msg}\n${fallbackMsg}`;
  const name = error instanceof Error && error.name
    ? error.name
    : /\bSyntaxError\b/.test(fallbackMsg) ? 'SyntaxError'
    : /\bReferenceError\b/.test(fallbackMsg) ? 'ReferenceError' : '';
  const fromOurChunk = sourceFile.includes('/_next/static/');

  if (name === 'SyntaxError') {
    if (/JSON/i.test(text)) return false;
    return fromOurChunk && !hasCallFrames(stack, name);
  }
  if (name === 'ReferenceError') {
    const missing = /([A-Za-z_$][\w$]*) is not defined/.exec(text)?.[1]
      ?? /Can't find variable: ([A-Za-z_$][\w$]*)/.exec(text)?.[1];
    if (missing !== undefined && LEGACY_MISSING_GLOBALS.has(missing)) return true;
    const touchesOurCode = stack.includes('/_next/static/') || fromOurChunk;
    // Nothing to judge by (no stack, no source file): log it rather than guess.
    if (!stack && !sourceFile) return false;
    return !touchesOurCode;
  }
  return false;
}

/**
 * Any call frame in the stack: V8 ("    at fn (url:1:2)") or JSC/SpiderMonkey ("fn@url:1:2"). V8 puts a
 * "Name: message" header first; Firefox/Safari do not, so only a line starting with the header is skipped.
 */
function hasCallFrames(stack: string, name: string): boolean {
  return stack.split('\n')
    .filter(line => !line.startsWith(`${name}:`))
    .some(line => /^\s*at\s/.test(line) || /@\S+:\d+/.test(line));
}

export function isWalletExtensionError(error: unknown, fallbackMsg = ''): boolean {
  const msg = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
  const stack = error instanceof Error ? (error.stack ?? '') : '';
  return matchesAny(msg, WALLET_EXTENSION_NEEDLES)
      || matchesAny(stack, WALLET_EXTENSION_NEEDLES)
      || matchesAny(fallbackMsg, WALLET_EXTENSION_NEEDLES);
}

// Errors thrown from browser-extension / userscript contexts — their stack
// frames live under an extension URL scheme (moz-extension://, chrome-extension://,
// safari-web-extension://). These are third-party injected scripts, not our code,
// so they're pure noise. Match the STACK: the message alone (e.g. "aResponse.match
// (...) is null") looks like an app error and would otherwise slip through.
const EXTENSION_SCHEME_REGEX = /(?:moz-extension|chrome-extension|safari-(?:web-)?extension):\/\//i;

export function isExtensionScriptError(error: unknown, fallbackMsg = ''): boolean {
  const msg = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
  const stack = error instanceof Error ? (error.stack ?? '') : '';
  return EXTENSION_SCHEME_REGEX.test(msg) || EXTENSION_SCHEME_REGEX.test(stack) || EXTENSION_SCHEME_REGEX.test(fallbackMsg);
}

// Stale-deploy chunk-load failures: user's tab predates the latest deploy and
// the old chunk URL 404s on navigation. Next.js retries on the next nav, so
// there's no user-visible impact — just noise in error_logs. Trade-off: a real
// chunking regression would also be silenced.
const CHUNK_LOAD_REGEX = /Failed to load chunk .* from module/i;

export function isChunkLoadError(error: unknown, fallbackMsg = ''): boolean {
  const msg = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
  const stack = error instanceof Error ? (error.stack ?? '') : '';
  return CHUNK_LOAD_REGEX.test(msg) || CHUNK_LOAD_REGEX.test(stack) || CHUNK_LOAD_REGEX.test(fallbackMsg);
}

// A permissions-policy / Privacy-Sandbox denial. Chrome rejects features blocked
// by policy with a SecurityError/NotAllowedError DOMException whose message is
// "The request was denied." — fired by the global gtag script, not our code (the
// level-dat audit found no clipboard / File-System / permission API anywhere in
// the app). Matched POSITIVELY on the DOMException's own name+message (present in
// every browser) rather than by absence of a stack frame, so a genuine app error
// is never suppressed. Pass the RAW rejection reason before it is wrapped.
export function isPermissionPolicyDenial(reason: unknown): boolean {
  return typeof DOMException !== 'undefined'
    && reason instanceof DOMException
    && (reason.name === 'SecurityError' || reason.name === 'NotAllowedError')
    && reason.message.includes('The request was denied');
}

// Browser-extension messaging chatter and other non-actionable third-party noise.
// Extensions inject content scripts that talk to their background page via
// chrome.runtime; when the page/tab lifecycle races that channel they throw
// rejections that bubble to our global handler. None of this is our code. The
// _getOwnPropertyDescriptor needle catches an Edge extension that proxies property
// access: its stack recurses through it and surfaces as a generic "Maximum call
// stack size exceeded"; that symbol never appears in our own bundle, so a genuine
// app stack-overflow (whose stack carries our minified frames) is still logged.
const EXTENSION_NOISE_NEEDLES = [
  'Internal JSON-RPC error',           // web3 wallet (JSON-RPC code -32603)
  'Key not found: tabId',
  'runtime.sendMessage',
  'No Listener:',
  '_getOwnPropertyDescriptor',         // extension property-proxy recursion (appears in stack)
  'contentScriptData',                 // extension content-script global
  "meta[property='og:type']",          // injected SEO/preview script reading OG tags
  'Lock was stolen by another request',// Supabase auth Web Lock contention across tabs
  'Lock broken by another request',    // same contention, the OTHER phrasing auth-js emits
  'browser.storage.local',             // Firefox/Safari extension storage API — never ours
];

/**
 * Errors thrown by script an extension INJECTED into our document.
 *
 * Our own code only ever runs from /_next/static/chunks/*.js, and we author no
 * inline event attributes at all — verified against the served HTML, which has
 * zero `on*=` attributes. So a frame like
 *
 *   at HTMLInputElement.onchange (https://mcworldtools.com/tools/level-dat:107:3)
 *
 * is impossible from our source: it is an inline handler, at a document line
 * past the end of the document we actually serve. Seen 2026-08-07 as
 * "Cannot read properties of undefined (reading 'location')" on both save-file
 * editors from one Chrome session — an extension hooking the file input.
 *
 * Narrow on purpose: keyed to the inline-handler frame, not to the message, so
 * a genuine TypeError of our own is untouched.
 */
const INLINE_HANDLER_FRAME = /at HTML\w*Element\.on\w+ \(/;

export function isInjectedInlineHandlerError(error: unknown): boolean {
  const stack = error instanceof Error ? (error.stack ?? '') : '';
  // Both conditions, same rule as isForeignStackOverflow: an inline-handler
  // frame AND no frame of ours anywhere. If our bundle is in the stack it is
  // our problem regardless of how the handler was attached.
  return INLINE_HANDLER_FRAME.test(stack) && !stack.includes('/_next/static/');
}

/**
 * "Maximum call stack size exceeded" whose stack contains NONE of our frames.
 *
 * This message has been triaged as environmental four times (level-dat, id-list,
 * uuid-lookup twice) and is dominated by Safari, whose stack limit is far lower
 * than Chrome's. The 2026-08-03 pair recursed `Fk@ Dk@ …` with no source URL on
 * any frame, on `/` and execute-builder alike — i.e. not tool-specific.
 *
 * Deliberately NOT a blanket message match: this repo has real recursion history
 * (loot-table entry nesting, NBT tree depth), and a genuine app overflow carries
 * /_next/static/ frames, so it still gets logged. The cost of this trade-off is
 * that an overflow with a totally empty stack is dropped — but that carries no
 * actionable information anyway.
 */
/**
 * The page is being served THROUGH a URL-rewriting web proxy (school "unblocker" services and the like).
 * The proxy fetches our scripts from its own host with our URL percent-encoded inside its path —
 * `…cloudfront.net/s/…/https%3A%2F%2Fmcworldtools.com%2F_next%2Fstatic%2Fchunks%2F…` — and wraps the page's
 * globals, so errors from its rewriting land on our board. Production #420/#421 (2026-09-17): the same
 * `Cannot read properties of undefined (reading 'length')` on /signup and /tools/mob-builder, three
 * minutes apart, both raised inside the proxy's `sj.js`. Nothing we ship ever loads its own chunks
 * percent-encoded, so that encoded path is a precise signature — a real error in our code (plain
 * `/_next/static/` frames, our origin) never matches.
 *
 * Second signature (#447–#456, 2026-10-03…09, ten tools, ~44 hits): the NightProxy "unblocker", whose
 * script is served from `cdn.jsdelivr.net/gh/NightProxy/…` and whose wrapper names (`8d6y8o.is`,
 * `jp`) are its own. Every one is "Cannot read properties of undefined (reading 'url')" with NO frame
 * of ours. Dropped only when the NightProxy script is in the stack (or is the error's file) AND no
 * plain `/_next/static/` frame is — a real error of ours raised under the proxy still logs.
 */
export function isProxiedSessionError(error: unknown, sourceFile = ''): boolean {
  const stack = error instanceof Error ? (error.stack ?? '') : '';
  const encodedChunk = /%2F_next%2Fstatic%2F/i;
  if (encodedChunk.test(stack) || encodedChunk.test(sourceFile)) return true;
  const nightProxy = /\/gh\/NightProxy\//i;
  return (nightProxy.test(stack) || nightProxy.test(sourceFile)) && !stack.includes('/_next/static/');
}

export function isForeignStackOverflow(error: unknown, fallbackMsg = ''): boolean {
  const msg = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
  const stack = error instanceof Error ? (error.stack ?? '') : '';
  const isOverflow = msg.includes('Maximum call stack size exceeded')
    || fallbackMsg.includes('Maximum call stack size exceeded');
  return isOverflow && !stack.includes('/_next/static/');
}

export function isExtensionNoiseError(error: unknown, fallbackMsg = ''): boolean {
  const msg = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
  const stack = error instanceof Error ? (error.stack ?? '') : '';
  return matchesAny(msg, EXTENSION_NOISE_NEEDLES)
      || matchesAny(stack, EXTENSION_NOISE_NEEDLES)
      || matchesAny(fallbackMsg, EXTENSION_NOISE_NEEDLES);
}

// Browsers throw a generic error for ALL fetch failures — transient network drops
// as well as CORS / CSP / DNS errors. We suppress them as noise: a client that
// closes a tab mid-request or has flaky wifi would otherwise flood error_logs.
// Trade-off (same as isChunkLoadError): a *systematic* failure — e.g. a
// misconfigured API origin CORS-blocking every request — is also silenced, so
// real outages must be caught via server logs / tool_events, not this client table.
//   Chromium: "Failed to fetch"   Safari: "Load failed"
//   Firefox:  "NetworkError when attempting to fetch resource."
// Anchored to the whole message, or the trailing reason of a wrapped rejection
// (…message=Load failed), so a hand-written "Failed to fetch world list" still logs.
// NOTE: we deliberately do NOT suppress our own "Failed to load image: <path>"
// errors — a persistently broken texture path is an actionable data bug.
const NETWORK_DROP_REGEX =
  /(?:^|message=)(?:Failed to fetch|Load failed|NetworkError when attempting to fetch resource)\.?$/;

export function isTransientNetworkError(error: unknown, fallbackMsg = ''): boolean {
  const msg = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
  return NETWORK_DROP_REGEX.test(msg) || NETWORK_DROP_REGEX.test(fallbackMsg);
}

/**
 * Wrap a value that is NOT an Error (a string rejection reason, a thrown object, a window error with no
 * `error`) for logging. `new Error()` records where it was CONSTRUCTED as its stack — our handler, in a
 * `/_next/static/` chunk — so the row claimed our code and every "no frame of ours" filter above was
 * blinded. Production #423–#443 (2026-09-24): 21 rejections from an assistive-literacy extension, each
 * stored with the single frame `at handleUnhandledRejection (…/_next/static/chunks/…)`.
 * The stack instead says what happened and carries no call frame — except `origin`, the one real
 * location a window error reports (`event.filename:lineno:colno`), which is kept because it is true and
 * because it keeps a non-Error thrown from OUR chunk recognisably ours.
 */
export const SYNTHETIC_STACK_NOTE = '[no stack: the value was not an Error]';
export function syntheticError(message: string, kind: string, origin?: string): Error {
  const e = new Error(message);
  const stack = `Error: ${message}\n    ${SYNTHETIC_STACK_NOTE} (${kind})${origin ? `\n    from ${origin}` : ''}`;
  // `stack` is an accessor on some engines; a failed override must not break error reporting.
  try { Object.defineProperty(e, 'stack', { value: stack, configurable: true, writable: true }); } catch { /* keep native */ }
  return e;
}

/**
 * A browser extension's content-script RPC timing out: "Request timeout <object>.<method>" (#423–#443),
 * or its bare "Request timeout isDictateAvailable" variant. We author no such message. Anchored to the
 * observed identifier shape so a sentence ("Request timeout after 30s"), a number or a multi-dot host still logs,
 * and an error of this text carrying one of OUR frames still logs.
 */
/**
 * An automation bridge's remote-object call failing: "Object Not Found Matching Id:2, MethodName:update,
 * ParamCount:4" (#445, on /tools/mob-builder, a string rejection with no stack). That is the message
 * format of a host-app JavaScript bridge (embedded webviews and link-preview scanners inject one and
 * call into objects that no longer exist) — we author no such text. Anchored to the exact shape, and
 * an error of this text carrying one of OUR frames still logs.
 */
const AUTOMATION_BRIDGE = /^Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+$/;
export function isAutomationBridgeError(error: unknown): boolean {
  const msg = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
  const stack = error instanceof Error ? (error.stack ?? '') : '';
  return AUTOMATION_BRIDGE.test(msg) && !stack.includes('/_next/static/');
}

const EXTENSION_RPC_TIMEOUT = /^Request timeout [A-Za-z_$][\w$-]*(?:\.[A-Za-z_$][\w$]*)?$/;
export function isExtensionRpcTimeout(error: unknown): boolean {
  const msg = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
  const stack = error instanceof Error ? (error.stack ?? '') : '';
  return EXTENSION_RPC_TIMEOUT.test(msg) && !stack.includes('/_next/static/');
}

